import React, { useState, useEffect, useContext, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AlertContext from "../../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../../Files/BaseURL";
import ThemeContext from "../../../../Context/theme/ThemeContext";
import axios from "axios";
import debounce from "lodash.debounce";

const MyJobOffer = () => {
  const { theme } = useContext(ThemeContext);
  const { showAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const [jobDetails, setJobDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  const formatDate = (timestamp) => {
    const dateObj = new Date(timestamp);
    const month = dateObj.toLocaleString("en-US", { month: "short" });
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${day} ${month}`;
  };

  const handleResponseDetail = (jobId, responseCount) => {
    if (responseCount === 0) {
      showAlert("No response yet", "danger");
      return;
    }
    navigate(`/user-dashboard/offers/my-job-requests/${jobId}`);
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${BaseURL}/api/requestjob/getcategory`);
      if (response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      showAlert("Error fetching categories", "danger");
    }
  }, [showAlert]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchJobs = useCallback(
    async (page = currentPage) => {
      try {
        if (page === 1) {
          setTableLoading(true);
        } else {
          setLoading(true);
        }
        setError(null);
        const authToken = localStorage.getItem("p@token");
        const response = await axios.get(
          `${BaseURL}/api/requestjob/getAllMyJobDetails`,
          {
            headers: { "auth-token": authToken },
            params: {
              page,
              limit: itemsPerPage,
              search: searchTerm,
              category: selectedCategory,
            },
          }
        );

        if (response.data.success) {
          setJobDetails(response.data.jobDetails || []);
          setTotalPages(response.data.pagination.totalPages);
          setTotalJobs(response.data.pagination.totalJobs);
        } else {
          showAlert(response.data.message || "Error fetching jobs", "danger");
          setError(response.data.message);
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Internal Server error";
        showAlert(errorMessage, "danger");
        setError(errorMessage);
      } finally {
        setTableLoading(false);
        setLoading(false);
      }
    },
    [showAlert, searchTerm, selectedCategory, itemsPerPage]
  );

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      if (value || searchTerm) {
        setCurrentPage(1);
        fetchJobs(1);
      }
    }, 300),
    [fetchJobs, searchTerm]
  );

  useEffect(() => {
    if (!searchTerm) {
      setCurrentPage(1);
      fetchJobs(1);
    }
  }, [searchTerm, fetchJobs]);

  useEffect(() => {
    fetchJobs(currentPage);
  }, [currentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primarygreen"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 font-pop text-lg font-medium text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row justify-between font-pop lg:items-center">
        <div className="flex flex-col md:flex-row gap-2 md:gap-6 md:items-center">
          <h3
            className={`text-gray-400 text-sm md:text-lg font-medium ${
              theme === "dark" && "text-white"
            }`}
          >
            Total {totalJobs} Offers
          </h3>
          <div
            className={`rounded-2xl px-1 md:px-3 ${
              theme === "dark" ? "bg-black text-white" : "bg-gray-300"
            }`}
          >
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`border-none outline-none w-full text-sm md:text-lg ${
                theme === "dark" ? "bg-black text-white" : "bg-gray-300"
              }`}
            >
              <option value="">All Category</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          className={`flex gap-3 items-center rounded-full py-1 px-4 ${
            theme === "dark" ? "bg-black text-white" : "bg-gray-300"
          }`}
        >
          <FaSearch className="text-gray-400 text-lg md:text-2xl" />
          <input
            type="text"
            placeholder="Search..."
            onChange={(e) => {
              e.preventDefault();
              debouncedSearch(e.target.value);
            }}
            className={`text-sm md:text-lg border-none outline-none ${
              theme === "dark" ? "bg-black text-white" : "bg-gray-300"
            }`}
          />
        </div>
      </div>
      <div className="overflow-x-scroll font-pop w-full my-2 md:my-6">
        <table
          className={`w-full border-spacing-y-4 border-separate ${
            theme === "dark" && "text-white"
          }`}
        >
          <thead>
            <tr>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left font-medium uppercase">
                Response
              </th>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left w-[30%] font-medium uppercase">
                Title
              </th>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left font-medium uppercase">
                Category
              </th>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left font-medium uppercase">
                Budget
              </th>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left font-medium uppercase">
                Industry
              </th>
              <th className="px-3 md:px-6 py-1 md:py-3 text-left font-medium uppercase">
                Delivery by
              </th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primarygreen mx-auto"></div>
                  </div>
                </td>
              </tr>
            ) : (
              jobDetails?.map((jobDetail, index) => {
                if (!jobDetail?.job) return null;

                return (
                  <tr
                    key={jobDetail.job._id || index}
                    onClick={() =>
                      handleResponseDetail(
                        jobDetail.job._id,
                        jobDetail.job.responseCount
                      )
                    }
                    className="cursor-pointer"
                  >
                    <td
                      className={`px-2 md:px-6 my-3 py-1 md:py-3 rounded-l-xl ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <div className="rounded-full bg-primarygreen inline-flex items-center justify-center px-2 min-w-[2rem] min-h-[2rem]">
                        <h2>{jobDetail.job.responseCount || 0}</h2>
                      </div>
                    </td>
                    <td
                      className={`px-2 md:px-6 my-3 text-base md:text-lg font-semibold py-3 text-left w-max min-w-[30%] ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <h2 className="w-max">{jobDetail.job.Title}</h2>
                    </td>
                    <td
                      className={`px-2 md:px- 6 my-3 py-1 md:py-3 font-normal text-left ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <h2 className="w-max">{jobDetail.skill}</h2>
                    </td>
                    <td
                      className={`px-2 md:px-6 my-3 py-1 md:py-3 text-left font-semibold ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <h2 className="w-max">${jobDetail.job.amount}</h2>
                    </td>
                    <td
                      className={`px-2 md:px-6 my-3 py-1 md:py-3 font-normal text-left ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <h2 className="w-max">{jobDetail.job.Category.name}</h2>
                    </td>
                    <td
                      className={`px-2 md:px-6 rounded-r-2xl text-gray-400 my-3 py-1 md:py-3 text-left ${
                        theme === "dark" ? "bg-black text-white" : "bg-white"
                      }`}
                    >
                      <h2 className="w-max">
                        {formatDate(jobDetail.job.DeliveryDate)}
                      </h2>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {jobDetails.length > 0 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4 rounded-lg">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              } ring-1 ring-inset ring-gray-300 rounded-md`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-semibold ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              } ring-1 ring-inset ring-gray-300 rounded-md`}
            >
              Next
            </button>
          </div>

          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-4 font-bold font-pop text-xs">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="block w-20 rounded-md border-0 py-1.5 pl-3 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primarygreen sm:text-sm sm:leading-6"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <p className="text-sm text-gray-700">items per page</p>
              </div>
            </div>

            <div className="font-pop text-xs font-bold">
              <p className="text-sm text-gray-700">
                {`${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
                  currentPage * itemsPerPage,
                  totalJobs
                )} of ${totalJobs} items`}
              </p>
            </div>

            <div className="flex items-center gap-4 font-bold font-pop text-xs">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-700">Page</p>
                <p className="text-sm font-bold text-gray-900">{currentPage}</p>
                <p className="text-sm text-gray-700">of</p>
                <p className="text-sm font-bold text-gray-900">{totalPages}</p>
                <p className="text-sm text-gray-700">pages</p>
              </div>
              <nav
                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                aria-label="Pagination"
              >
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  } ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0`}
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  } ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0`}
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {!tableLoading && jobDetails.length === 0 && (
        <div className="text-center py-4 font-pop text-lg font-medium">
          No jobs found
        </div>
      )}
    </div>
  );
};

export default MyJobOffer;
